import type { Aisle } from './aisleLabels.ts';

export interface AisleSection<T> {
  aisle: Aisle;
  items: T[];
}

/**
 * Groups already-sorted items under the aisles they belong to.
 *
 * Takes the items in `sortOrder` and keeps them that way <i>within</i> each section, so an
 * aisle reads in the order things were added to it exactly as the flat list does. The
 * headings change where a row sits on the screen, never where it sits relative to the rest
 * of its aisle — which is what lets the same setting be turned on and off without anything
 * appearing to shuffle.
 *
 * Empty aisles are dropped rather than drawn with nothing under them. On an ordinary shop
 * three or four sections have anything in them, and a screen of headings over an empty
 * column is a longer list saying less.
 *
 * The resolver is passed in rather than imported, and the items are only required to have a
 * name — so this is a pure function of its arguments, and a test can hand it a stub instead
 * of the shipped table. It lives here rather than in `ListView` because that file exports a
 * component and may therefore export nothing else; see the Fast Refresh note in CLAUDE.md.
 */
export function sectionise<T extends { name: string }>(
  items: T[],
  order: readonly Aisle[],
  resolve: (name: string) => Aisle,
): AisleSection<T>[] {
  const byAisle = new Map<Aisle, T[]>();

  for (const item of items) {
    const aisle = resolve(item.name);
    const existing = byAisle.get(aisle);

    if (existing) {
      existing.push(item);
    } else {
      byAisle.set(aisle, [item]);
    }
  }

  return order.flatMap((aisle) => {
    const found = byAisle.get(aisle);
    return found ? [{ aisle, items: found }] : [];
  });
}
